import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import type {
  ProblemCategory,
  RegisterLine,
  RegisterResponse,
  WorkTypeOption,
} from '@/api/types'

type GroupBy = 'employee' | 'job' | 'none' | 'day'

const GROUPS: { key: GroupBy; label: string; hint: string }[] = [
  { key: 'employee', label: 'By employee', hint: 'What each person will be paid' },
  { key: 'job', label: 'By job', hint: 'What each job absorbed in labor' },
  { key: 'none', label: 'Pay period', hint: 'Everything in the date range, flat' },
  { key: 'day', label: 'By crew day', hint: 'As each day was submitted' },
]

/**
 * The labor register.
 *
 * Deliberately shaped like Sage's Time Track Entry — same columns, same
 * formatting (800-000-000, 21-F0276) — because accounting reviews this screen
 * against that one and translating between two layouts is where mistakes get
 * made.
 *
 * One grid, four groupings. What changes between the views is the sort and the
 * subtotal, not the data, so a correction made in one is visible in all.
 */
export function RegisterPage() {
  const [data, setData] = useState<RegisterResponse | null>(null)
  const [workTypes, setWorkTypes] = useState<WorkTypeOption[]>([])
  const [groupBy, setGroupBy] = useState<GroupBy>('employee')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [unpostedOnly, setUnpostedOnly] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState({ value: '', workTypeId: 0 })
  /**
   * Adding a line to an existing crew day, from the row it belongs beside.
   *
   * The reason this is here and not only on the crew-day screen: the overtime
   * flag is worked from this register. Someone sees a week that needed
   * overtime, trims the Regular hours on one row, and owes an Overtime line on
   * the same day for the same person. Sending them to another screen to add it
   * is where the second half gets forgotten.
   */
  const [addingTo, setAddingTo] = useState<string | null>(null)
  const [addDraft, setAddDraft] = useState({ workTypeId: 0, value: '' })
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  /**
   * Overtime still owed on the weeks about to be exported.
   *
   * This screen is the last stop before accounting builds the Sage import —
   * its own docstring calls the unposted view "what accounting works through
   * before building the import file". Since the field can no longer record
   * overtime at all, every hour here is straight time until somebody says
   * otherwise, and once a week posts the flag drops out of the check.
   *
   * So the warning belongs HERE, at the moment someone is about to export,
   * rather than only on a screen they have to remember to open.
   */
  const [otReview, setOtReview] = useState<ProblemCategory | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [r, w] = await Promise.all([
        api.register({
          date_from: from || undefined,
          date_to: to || undefined,
          unposted_only: unpostedOnly,
        }),
        api.workTypes(false),
      ])
      setData(r)
      setWorkTypes(w)

      // Only for the pre-export view. On the general register it would be a
      // standing notice nobody reads.
      if (unpostedOnly) {
        try {
          setOtReview(await api.problem('time.needs_ot_review'))
        } catch {
          setOtReview(null)
        }
      } else {
        setOtReview(null)
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the register')
    }
  }, [from, to, unpostedOnly])

  useEffect(() => {
    void load()
  }, [load])

  /** Group and subtotal. Sorting already comes back employee-then-date. */
  const groups = useMemo(() => {
    const lines = data?.lines ?? []
    if (groupBy === 'none') {
      return [{ key: 'all', heading: '', sub: '', lines }]
    }
    const map = new Map<string, { heading: string; sub: string; lines: RegisterLine[] }>()
    for (const l of lines) {
      let key: string
      let heading: string
      let sub: string
      if (groupBy === 'employee') {
        key = l.sage_employee_key
        heading = l.display_name
        sub = l.employee_display ?? ''
      } else if (groupBy === 'job') {
        key = l.job_no
        heading = l.job_no
        sub = l.job_description ?? ''
      } else {
        key = l.submission_id
        heading = `${l.job_no} · ${l.date_worked}`
        sub = l.submitted_by ?? ''
      }
      if (!map.has(key)) map.set(key, { heading, sub, lines: [] })
      map.get(key)!.lines.push(l)
    }
    return [...map.entries()].map(([key, v]) => ({ key, ...v }))
  }, [data, groupBy])

  function startEdit(l: RegisterLine) {
    setEditing(l.time_entry_id)
    setDraft({
      value: String(l.entry_type === 'Hours' ? (l.hours_worked ?? '') : (l.amount ?? '')),
      workTypeId: l.work_type_id ?? 0,
    })
    setError(null)
  }

  /** Overtime is the reason this exists, so it is the default when present. */
  function startAdd(l: RegisterLine) {
    const ot = workTypes.find((w) => w.label === 'Overtime')
    setAddingTo(l.time_entry_id)
    setEditing(null)
    setNotice(null)
    setAddDraft({ workTypeId: ot?.work_type_id ?? workTypes[0]?.work_type_id ?? 0, value: '' })
  }

  async function addLine(l: RegisterLine) {
    const wt = workTypes.find((w) => w.work_type_id === addDraft.workTypeId)
    if (!wt) return
    const n = Number(addDraft.value)
    if (!n || n <= 0) {
      return setError('Enter hours or an amount for the new line.')
    }
    setBusy(true)
    setError(null)
    try {
      await api.addEntry(l.submission_id, {
        sage_employee_key: l.sage_employee_key,
        work_type_id: wt.work_type_id,
        labor_code: l.labor_code,
        // Only the side this work type calls for; the server rejects both.
        hours_worked: wt.entry_type === 'Hours' ? n : null,
        amount: wt.entry_type === 'Amount' ? n : null,
        reason: reason || 'Added from the labor register',
      })
      setAddingTo(null)
      setReason('')
      setNotice(`${wt.label} added for ${l.display_name} on ${l.date_worked}.`)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add the line')
    } finally {
      setBusy(false)
    }
  }

  async function save(l: RegisterLine) {
    setBusy(true)
    setError(null)
    try {
      const wt = workTypes.find((w) => w.work_type_id === draft.workTypeId)
      const type = wt?.entry_type ?? l.entry_type
      const num = Number(draft.value)
      await api.correctEntry(l.submission_id, l.time_entry_id, {
        work_type_id: draft.workTypeId || null,
        hours_worked: type === 'Hours' ? num : null,
        amount: type === 'Amount' ? num : null,
        reason: reason || null,
      })
      setEditing(null)
      await load()
      setNotice('Correction saved and recorded in the audit trail.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  /** Remove a line that should not have been recorded.
   *  Refused server-side once posted, and written to the audit trail either
   *  way — a deleted line keeps its history. */
  async function remove(l: RegisterLine) {
    const what =
      l.entry_type === 'Hours'
        ? `${Number(l.hours_worked).toFixed(2)} hrs`
        : `$${Number(l.amount).toFixed(2)}`
    if (
      !confirm(
        `Delete ${l.display_name}'s ${l.work_type_label ?? l.earnings_code} line ` +
          `(${what}) on ${l.date_worked}?`,
      )
    )
      return
    setBusy(true)
    setError(null)
    try {
      await api.deleteEntry(l.submission_id, l.time_entry_id, reason || undefined)
      await load()
      setNotice('Line deleted and recorded in the audit trail.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete the line')
    } finally {
      setBusy(false)
    }
  }

  const num = (v: number | null) => (v == null ? '' : Number(v).toFixed(2))

  const subtotal = (lines: RegisterLine[]) => ({
    hours: lines.reduce((a, l) => a + Number(l.hours_worked ?? 0), 0),
    amount: lines.reduce((a, l) => a + Number(l.amount ?? 0), 0),
    estimated: lines.reduce((a, l) => a + Number(l.estimated_amount ?? 0), 0),
  })

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-info">{notice}</div>}

      {/* The pre-export stop. Every hour on this screen is straight time
          unless somebody applied overtime, and once a week posts to Sage the
          check can no longer see it — so this is the last point at which the
          omission is still fixable. */}
      {otReview && otReview.count > 0 && (
        <div className="alert alert-error">
          <strong>
            {otReview.count} week{otReview.count === 1 ? '' : 's'} still need
            {otReview.count === 1 ? 's' : ''} overtime applied.
          </strong>{' '}
          The field records every hour at straight time. Exporting now pays
          these at straight time, and once the week posts it can only be fixed
          in Sage.
          <ul style={{ margin: '0.5rem 0 0.5rem 1.1rem', fontSize: '0.8125rem' }}>
            {otReview.rows.slice(0, 6).map((r) => (
              <li key={r.entity_key}>
                {r.title} — {r.detail}
              </li>
            ))}
            {otReview.rows.length > 6 && (
              <li className="muted">
                and {otReview.rows.length - 6} more
              </li>
            )}
          </ul>
          <Link to="/office/problems">Review these on Problems →</Link>
        </div>
      )}

      <div className="card">
        <h2>Labor register</h2>
        <p className="muted">
          Recorded time in the same layout as Sage Time Track Entry, so this
          screen and that one read the same way. Correct anything not yet sent
          to Sage directly in the grid.
        </p>
        <p className="muted" style={{ fontSize: '0.8125rem' }}>
          <strong>Est. Rate</strong> comes from the employee card —
          rate&nbsp;1 or&nbsp;2 times the earnings code multiplier. Sage
          calculates the real rate at import. A rate marked{' '}
          <strong>?</strong> and greyed is one the card is known not to
          predict: prevailing wage jobs take the job rate, and rider rates
          follow a rule we have not confirmed. Treat the column as a sanity
          check, not a figure to reconcile against.
        </p>

        <div className="segmented" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          {GROUPS.map((g) => (
            <button
              key={g.key}
              type="button"
              aria-selected={groupBy === g.key}
              title={g.hint}
              onClick={() => setGroupBy(g.key)}
            >
              {g.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label htmlFor="from">From</label>
            <input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label htmlFor="to">To</label>
            <input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label>
              <input
                type="checkbox"
                checked={unpostedOnly}
                onChange={(e) => setUnpostedOnly(e.target.checked)}
                style={{ minHeight: 'auto', marginRight: 8 }}
              />
              Not yet sent to Sage
            </label>
          </div>
        </div>

        {data && (
          <p className="muted" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
            <strong>{data.totals.lines}</strong> lines ·{' '}
            <strong>{data.totals.employees}</strong> employees ·{' '}
            <strong>{data.totals.jobs}</strong> jobs ·{' '}
            <strong>{Number(data.totals.total_hours).toFixed(2)}</strong> hours
            {Number(data.totals.total_amount) > 0 && (
              <> · <strong>${Number(data.totals.total_amount).toFixed(2)}</strong></>
            )}
            {data.truncated && (
              <span className="badge badge-warn" style={{ marginLeft: 8 }}>
                truncated — narrow the dates
              </span>
            )}
          </p>
        )}
      </div>

      <div className="card">
        <h2>Reason for corrections</h2>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Recorded against every change made while this is filled in"
          maxLength={300}
        />
      </div>

      {data === null ? (
        <div className="card"><p className="muted">Loading…</p></div>
      ) : data.lines.length === 0 ? (
        <div className="card">
          <p className="muted">No recorded time matches these filters.</p>
        </div>
      ) : (
        groups.map((g) => {
          const st = subtotal(g.lines)
          return (
            <div className="card" key={g.key}>
              {g.heading && (
                <div className="crew-heading">
                  <h2 className="title" style={{ margin: 0 }}>
                    {g.heading}{' '}
                    <span className="mono muted" style={{ fontWeight: 400, fontSize: '0.875rem' }}>
                      {g.sub}
                    </span>
                  </h2>
                  <span className="count-pill">{st.hours.toFixed(2)} h</span>
                </div>
              )}
              <div className="table-wrap table-dense">
                <table>
                  <thead>
                    <tr>
                      <th>Date Worked</th>
                      {groupBy !== 'employee' && <th>Employee</th>}
                      {groupBy !== 'job' && <th>Job Number</th>}
                      <th>Wage</th>
                      <th>Labor Code</th>
                      <th>Cost Code</th>
                      <th>Description</th>
                      <th>Earnings Code</th>
                      <th>Shift</th>
                      <th>Dept</th>
                      <th className="num">Hours Worked</th>
                      <th className="num">Est. Rate</th>
                      <th className="num">Est. Amount</th>
                      <th className="num">Amount</th>
                      <th>Sage</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.lines.map((l) => {
                      const isEditing = editing === l.time_entry_id
                      const locked = l.post_status !== 'NotPosted'
                      const isAdding = addingTo === l.time_entry_id
                      return (
                        <Fragment key={l.time_entry_id}>
                        {isAdding && (
                          <tr>
                            <td colSpan={99} style={{ background: 'var(--c-surface-alt)' }}>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <strong style={{ fontSize: '0.875rem' }}>
                                  Add a line for {l.display_name} — {l.date_worked}
                                </strong>
                                <select
                                  value={addDraft.workTypeId}
                                  onChange={(e) =>
                                    setAddDraft({ ...addDraft, workTypeId: Number(e.target.value) })
                                  }
                                  aria-label="Work type for the new line"
                                >
                                  {workTypes.map((w) => (
                                    <option key={w.work_type_id} value={w.work_type_id}>
                                      {w.label}
                                    </option>
                                  ))}
                                </select>
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  step="0.25"
                                  min="0"
                                  style={{ maxWidth: 110 }}
                                  value={addDraft.value}
                                  onChange={(e) => setAddDraft({ ...addDraft, value: e.target.value })}
                                  placeholder={
                                    workTypes.find((w) => w.work_type_id === addDraft.workTypeId)
                                      ?.entry_type === 'Amount' ? '$' : 'hrs'
                                  }
                                  aria-label="Hours or amount for the new line"
                                />
                                <button className="btn" style={{ minHeight: 30 }}
                                  disabled={busy} onClick={() => void addLine(l)}>
                                  Add
                                </button>
                                <button className="btn btn-secondary" style={{ minHeight: 30 }}
                                  onClick={() => setAddingTo(null)}>
                                  Cancel
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td>{l.date_worked}</td>
                          {groupBy !== 'employee' && (
                            <td>
                              {l.display_name}
                              <div className="mono muted" style={{ fontSize: '0.75rem' }}>
                                {l.employee_display}
                              </div>
                            </td>
                          )}
                          {groupBy !== 'job' && (
                            <td className="mono">
                              {l.job_no}
                              {/* This line was redirected off the crew-day's
                                  job — travel booked to the job they drove to.
                                  Without the marker the row looks like it came
                                  from a crew day on this job, and reconciling
                                  it against the daily sheet would not add up. */}
                              {l.override_job_no && (
                                <div
                                  className="muted"
                                  style={{ fontSize: '0.7rem' }}
                                  title="Travel charged to the job they drove to, not the job the crew day was opened on"
                                >
                                  ↳ travelled here
                                </div>
                              )}
                            </td>
                          )}
                          {/* Prevailing wage changes both the rate and what
                              certified payroll will need, so it belongs on the
                              row rather than buried in the job record. */}
                          <td>
                            {l.job_is_prevailing ? (
                              <span
                                className="badge badge-warn"
                                title={
                                  l.job_reg_wage
                                    ? `Job rate ${Number(l.job_reg_wage).toFixed(2)}` +
                                      (l.job_fringe
                                        ? ` + ${Number(l.job_fringe).toFixed(2)} fringe`
                                        : '')
                                    : 'Prevailing wage job with no rate set'
                                }
                              >
                                Prevailing
                              </span>
                            ) : l.job_is_certified ? (
                              <span className="badge badge-info" title="Certified payroll job">
                                Certified
                              </span>
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </td>
                          <td className="mono">{l.labor_code ?? '—'}</td>
                          {/* Cost code and its description are separate
                              columns: stacking them made the row two lines
                              deep and broke alignment with Sage's layout. */}
                          <td className="mono">
                            {isEditing ? (
                              <select
                                value={draft.workTypeId}
                                onChange={(e) =>
                                  setDraft({ ...draft, workTypeId: Number(e.target.value) })
                                }
                                aria-label="Work type"
                              >
                                {draft.workTypeId === 0 && <option value={0}>—</option>}
                                {workTypes.map((w) => (
                                  <option key={w.work_type_id} value={w.work_type_id}>
                                    {w.label}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              l.cost_code_display
                            )}
                          </td>
                          <td>{l.work_type_label ?? l.cost_code_description ?? '—'}</td>
                          <td className="mono">{l.earnings_code}</td>
                          <td className="mono muted">{l.shift_code ?? '—'}</td>
                          <td className="mono muted">{l.department_no ?? '—'}</td>
                          <td className="num">
                            {isEditing && l.entry_type === 'Hours' ? (
                              <input
                                type="number"
                                step="0.25"
                                min="0"
                                max="24"
                                value={draft.value}
                                onChange={(e) => setDraft({ ...draft, value: e.target.value })}
                                style={{ width: 80 }}
                                autoFocus
                              />
                            ) : (
                              num(l.hours_worked)
                            )}
                          </td>
                          {/* Estimated, not authoritative. Flagged where it is
                              known to be unreliable rather than shown as fact. */}
                          <td className="num">
                            {l.estimated_rate == null ? (
                              <span className="muted">—</span>
                            ) : (
                              <span
                                className={l.rate_reliable ? '' : 'muted'}
                                title={
                                  l.rate_note ??
                                  (l.rate_source === 'Job (prevailing wage)'
                                    ? `Job prevailing rate ${Number(l.job_reg_wage).toFixed(2)}` +
                                      (l.job_fringe
                                        ? ` + ${Number(l.job_fringe).toFixed(2)} fringe`
                                        : '') +
                                      ` × ${Number(l.rate_multiplier).toFixed(2)}`
                                    : `Rate ${l.pay_rate_no} × ${Number(l.rate_multiplier).toFixed(2)} from the employee card`)
                                }
                              >
                                {Number(l.estimated_rate).toFixed(3)}
                                {!l.rate_reliable && ' ?'}
                              </span>
                            )}
                          </td>
                          <td className="num">
                            {l.estimated_amount == null ? (
                              <span className="muted">—</span>
                            ) : (
                              <span className={l.rate_reliable ? '' : 'muted'}>
                                {Number(l.estimated_amount).toFixed(2)}
                              </span>
                            )}
                          </td>
                          <td className="num">
                            {isEditing && l.entry_type === 'Amount' ? (
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={draft.value}
                                onChange={(e) => setDraft({ ...draft, value: e.target.value })}
                                style={{ width: 90 }}
                                autoFocus
                              />
                            ) : (
                              num(l.amount)
                            )}
                          </td>
                          <td>
                            <span
                              className={`badge ${
                                l.post_status === 'Posted'
                                  ? 'badge-ok'
                                  : l.post_status === 'Failed'
                                    ? 'badge-error'
                                    : 'badge-info'
                              }`}
                            >
                              {l.post_status}
                            </span>
                          </td>
                          <td>
                            {locked ? (
                              <span className="muted" style={{ fontSize: '0.75rem' }}>
                                locked
                              </span>
                            ) : isEditing ? (
                              <div style={{ display: 'flex', gap: 4 }}>
                                <button
                                  className="btn"
                                  style={{ minHeight: 30 }}
                                  disabled={busy}
                                  onClick={() => void save(l)}
                                >
                                  Save
                                </button>
                                <button
                                  className="btn btn-secondary"
                                  style={{ minHeight: 30 }}
                                  onClick={() => setEditing(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: 4 }}>
                                <button
                                  className="btn btn-secondary"
                                  style={{ minHeight: 30 }}
                                  onClick={() => startEdit(l)}
                                >
                                  Edit
                                </button>
                                {/* Adds to the SAME crew day and the SAME
                                    person, which is what the overtime case
                                    needs and removes any chance of the line
                                    landing on the wrong day. */}
                                <button
                                  className="btn btn-secondary"
                                  style={{ minHeight: 30 }}
                                  onClick={() => startAdd(l)}
                                  title={`Add a line for ${l.display_name} on ${l.date_worked}`}
                                >
                                  + Line
                                </button>
                                <button
                                  className="btn btn-secondary"
                                  style={{ minHeight: 30 }}
                                  disabled={busy}
                                  onClick={() => void remove(l)}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                        </Fragment>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={groupBy === 'none' ? 10 : 9}>
                        <strong>Total</strong>
                      </td>
                      <td className="num"><strong>{st.hours.toFixed(2)}</strong></td>
                      <td></td>
                      <td className="num muted">
                        {st.estimated ? st.estimated.toFixed(2) : ''}
                      </td>
                      <td className="num">
                        <strong>{st.amount ? st.amount.toFixed(2) : ''}</strong>
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}
